import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Package, ShoppingCart, Ruler, Truck, Wrench, Lightbulb, 
  Shield, Paintbrush, Sparkles, Star, MapPin,
  Phone, Mail, MapPinIcon, MessageCircle, ArrowRight, Check,
  Filter, X
} from 'lucide-react';
import { containerTypes, services, features, projects, testimonials } from '../mock';

const iconMap = {
  Package, ShoppingCart, Ruler, Truck, Wrench, Lightbulb,
  Shield, Paintbrush, Sparkles, Star, MapPin
};

export const Home = () => {
  const [selectedSize, setSelectedSize] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [activeProject, setActiveProject] = useState('all');
  const [quoteForm, setQuoteForm] = useState({ name: '', email: '', phone: '', containerType: '', message: '' });
  const [contactForm, setContactForm] = useState({ name: '', email: '', phone: '', message: '' });

  const filteredContainers = containerTypes.filter(container => {
    const sizeMatch = selectedSize === 'all' || container.size === selectedSize;
    const typeMatch = selectedType === 'all' || container.type === selectedType;
    return sizeMatch && typeMatch;
  });

  const filteredProjects = activeProject === 'all' 
    ? projects 
    : projects.filter(project => project.category === activeProject);

  const handleQuoteSubmit = (e) => {
    e.preventDefault();
    console.log('Quote form submitted:', quoteForm);
    alert('¡Gracias! Nos pondremos en contacto contigo pronto.');
    setQuoteForm({ name: '', email: '', phone: '', containerType: '', message: '' });
  };

  const handleContactSubmit = (e) => {
    e.preventDefault();
    console.log('Contact form submitted:', contactForm);
    alert('¡Mensaje enviado! Te responderemos a la brevedad.');
    setContactForm({ name: '', email: '', phone: '', message: '' });
  };

  const openWhatsApp = () => {
    window.open('https://wa.me/573001234567?text=Hola%2C%20estoy%20interesado%20en%20sus%20contenedores', '_blank');
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center">
                <Package className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">TRION</h1>
                <p className="text-xs text-orange-600 font-medium">CONTAINERS</p>
              </div>
            </div>
            <nav className="hidden md:flex items-center space-x-8">
              <a href="#inicio" className="text-gray-700 hover:text-orange-600 transition-colors font-medium">Inicio</a>
              <a href="#servicios" className="text-gray-700 hover:text-orange-600 transition-colors font-medium">Servicios</a>
              <a href="#catalogo" className="text-gray-700 hover:text-orange-600 transition-colors font-medium">Catálogo</a>
              <a href="#proyectos" className="text-gray-700 hover:text-orange-600 transition-colors font-medium">Proyectos</a>
              <a href="#contacto" className="text-gray-700 hover:text-orange-600 transition-colors font-medium">Contacto</a>
              <Button className="bg-orange-600 hover:bg-orange-700 text-white" onClick={() => document.getElementById('cotizacion').scrollIntoView({ behavior: 'smooth' })}>
                Cotizar Ahora
              </Button>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section id="inicio" className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 via-orange-50/30 to-white relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-[0.02]"></div>
        <div className="container mx-auto relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-6 bg-orange-100 text-orange-700 hover:bg-orange-200 border-orange-200 px-4 py-2 text-sm font-medium">
              Espacios Modulares en toda Colombia
            </Badge>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 mb-6 leading-tight">
              Soluciones que <span className="text-orange-600">Construyen</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 mb-12 leading-relaxed max-w-3xl mx-auto">
              Alquiler y venta de contenedores marítimos de 20 y 40 pies. Oficinas, bodegas y proyectos personalizados en Medellín y toda Colombia.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button size="lg" className="bg-orange-600 hover:bg-orange-700 text-white px-8 py-6 text-lg font-semibold group" onClick={() => document.getElementById('catalogo').scrollIntoView({ behavior: 'smooth' })}>
                Ver Catálogo
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button size="lg" variant="outline" className="border-2 border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white px-8 py-6 text-lg font-semibold" onClick={openWhatsApp}>
                <MessageCircle className="mr-2 w-5 h-5" />
                WhatsApp
              </Button>
            </div>
          </div>
          <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="text-4xl font-bold text-orange-600 mb-2">500+</div>
              <div className="text-gray-600 font-medium">Proyectos Entregados</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-orange-600 mb-2">15+</div>
              <div className="text-gray-600 font-medium">Años de Experiencia</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-orange-600 mb-2">100%</div>
              <div className="text-gray-600 font-medium">Satisfacción Cliente</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-orange-600 mb-2">24/7</div>
              <div className="text-gray-600 font-medium">Soporte Técnico</div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="nosotros" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="container mx-auto">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">¿Por qué elegir TRION?</h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              Somos líderes en soluciones modulares con contenedores marítimos en Colombia. Nuestra sede principal en Medellín nos permite servir a todo el país con calidad y eficiencia.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature) => {
              const IconComponent = iconMap[feature.icon];
              return (
                <Card key={feature.id} className="border-2 border-gray-100 hover:border-orange-200 hover:shadow-lg transition-all duration-300 group">
                  <CardHeader>
                    <div className="w-14 h-14 bg-orange-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-orange-600 transition-colors duration-300">
                      <IconComponent className="w-7 h-7 text-orange-600 group-hover:text-white transition-colors duration-300" />
                    </div>
                    <CardTitle className="text-xl font-bold text-gray-900">{feature.title}</CardTitle>
                    <CardDescription className="text-gray-600 text-base">{feature.description}</CardDescription>
                  </CardHeader>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="servicios" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="container mx-auto">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Nuestros Servicios</h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              Ofrecemos soluciones integrales desde el diseño hasta la entrega e instalación de tu proyecto modular.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => {
              const IconComponent = iconMap[service.icon];
              return (
                <Card key={service.id} className="bg-white border-2 border-gray-100 hover:shadow-xl transition-all duration-300 hover:-translate-y-2 group">
                  <CardHeader>
                    <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                      <IconComponent className="w-8 h-8 text-white" />
                    </div>
                    <CardTitle className="text-xl font-bold text-gray-900 mb-3">{service.title}</CardTitle>
                    <CardDescription className="text-gray-600 text-base leading-relaxed">{service.description}</CardDescription>
                  </CardHeader>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Catalog Section */}
      <section id="catalogo" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="container mx-auto">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Catálogo de Contenedores</h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              Explora nuestra amplia selección de contenedores adaptados a cada necesidad.
            </p>
          </div>
          
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4 mb-12 max-w-2xl mx-auto">
            <Select value={selectedSize} onValueChange={setSelectedSize}>
              <SelectTrigger className="w-full sm:w-[200px] border-2 border-gray-200">
                <SelectValue placeholder="Tamaño" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los tamaños</SelectItem>
                <SelectItem value="20">20 pies</SelectItem>
                <SelectItem value="40">40 pies</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={selectedType} onValueChange={setSelectedType}>
              <SelectTrigger className="w-full sm:w-[200px] border-2 border-gray-200">
                <SelectValue placeholder="Tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los tipos</SelectItem>
                <SelectItem value="oficina">Oficina</SelectItem>
                <SelectItem value="bodega">Bodega</SelectItem>
                <SelectItem value="personalizado">Personalizado</SelectItem>
              </SelectContent>
            </Select>
            
            {(selectedSize !== 'all' || selectedType !== 'all') && (
              <Button variant="outline" onClick={() => { setSelectedSize('all'); setSelectedType('all'); }} className="border-2 border-gray-200">
                <X className="w-4 h-4 mr-2" />
                Limpiar filtros
              </Button>
            )}
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredContainers.map((container) => (
              <Card key={container.id} className="overflow-hidden border-2 border-gray-100 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 group">
                <div className="relative h-56 overflow-hidden bg-gray-100">
                  <img src={container.image} alt={container.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                  <Badge className="absolute top-4 right-4 bg-orange-600 text-white border-0 px-3 py-1">
                    {container.size} pies
                  </Badge>
                </div>
                <CardHeader>
                  <CardTitle className="text-xl font-bold text-gray-900 mb-2">{container.name}</CardTitle>
                  <CardDescription className="text-gray-600 mb-4">{container.description}</CardDescription>
                  <div className="space-y-2 mb-4">
                    {container.features.slice(0, 3).map((feature, idx) => (
                      <div key={idx} className="flex items-start gap-2">
                        <Check className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                  <div className="border-t border-gray-200 pt-4 mb-4">
                    <div className="flex items-baseline gap-2 mb-1">
                      <span className="text-sm text-gray-600">Alquiler:</span>
                      <span className="text-lg font-bold text-orange-600">{container.price}</span>
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-sm text-gray-600">Venta:</span>
                      <span className="text-lg font-bold text-gray-900">{container.salePrice}</span>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <Button className="w-full bg-orange-600 hover:bg-orange-700 text-white font-semibold" onClick={() => {
                    setQuoteForm({...quoteForm, containerType: container.name});
                    document.getElementById('cotizacion').scrollIntoView({ behavior: 'smooth' });
                  }}>
                    Cotizar este contenedor
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Gallery */}
      <section id="proyectos" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="container mx-auto">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Proyectos Realizados</h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              Conoce algunos de los proyectos que hemos materializado para nuestros clientes en toda Colombia.
            </p>
          </div>

          <Tabs defaultValue="all" className="w-full" onValueChange={setActiveProject}>
            <TabsList className="grid w-full max-w-2xl mx-auto grid-cols-3 lg:grid-cols-6 mb-12 bg-white border-2 border-gray-200 p-1">
              <TabsTrigger value="all" className="data-[state=active]:bg-orange-600 data-[state=active]:text-white">Todos</TabsTrigger>
              <TabsTrigger value="Oficina" className="data-[state=active]:bg-orange-600 data-[state=active]:text-white">Oficinas</TabsTrigger>
              <TabsTrigger value="Bodega" className="data-[state=active]:bg-orange-600 data-[state=active]:text-white">Bodegas</TabsTrigger>
              <TabsTrigger value="Comercial" className="data-[state=active]:bg-orange-600 data-[state=active]:text-white">Comercial</TabsTrigger>
              <TabsTrigger value="Vivienda" className="data-[state=active]:bg-orange-600 data-[state=active]:text-white">Vivienda</TabsTrigger>
              <TabsTrigger value="Eventos" className="data-[state=active]:bg-orange-600 data-[state=active]:text-white">Eventos</TabsTrigger>
            </TabsList>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredProjects.map((project) => (
                <Card key={project.id} className="overflow-hidden border-2 border-gray-100 hover:shadow-xl transition-all duration-300 hover:-translate-y-2 group bg-white">
                  <div className="relative h-56 overflow-hidden bg-gray-100">
                    <img src={project.image} alt={project.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-gradient-to-t from-gray-900/60 to-transparent"></div>
                    <Badge className="absolute bottom-4 left-4 bg-orange-600 text-white border-0 px-3 py-1">
                      {project.category}
                    </Badge>
                  </div>
                  <CardHeader>
                    <CardTitle className="text-xl font-bold text-gray-900 mb-2">{project.title}</CardTitle>
                    <CardDescription className="text-gray-600 mb-3">{project.description}</CardDescription>
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <MapPinIcon className="w-4 h-4 text-orange-600" />
                      <span>{project.location}</span>
                    </div>
                  </CardHeader>
                </Card>
              ))}
            </div>
          </Tabs>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonios" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="container mx-auto">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Lo que Dicen Nuestros Clientes</h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              La satisfacción de nuestros clientes es nuestro mayor logro.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="border-2 border-gray-100 hover:shadow-xl transition-all duration-300 bg-white">
                <CardHeader>
                  <div className="flex items-start gap-4 mb-4">
                    <img 
                      src={testimonial.image} 
                      alt={testimonial.name} 
                      className="w-16 h-16 rounded-full object-cover border-2 border-orange-200"
                    />
                    <div className="flex-1">
                      <CardTitle className="text-lg font-bold text-gray-900">{testimonial.name}</CardTitle>
                      <p className="text-sm text-gray-600">{testimonial.position}</p>
                      <p className="text-sm text-orange-600 font-medium">{testimonial.company}</p>
                    </div>
                    <div className="flex gap-1">
                      {[...Array(testimonial.rating)].map((_, idx) => (
                        <Star key={idx} className="w-5 h-5 fill-orange-500 text-orange-500" />
                      ))}
                    </div>
                  </div>
                  <CardDescription className="text-gray-700 leading-relaxed text-base italic">
                    "{testimonial.content}"
                  </CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Quote Form Section */}
      <section id="cotizacion" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-orange-50 to-white">
        <div className="container mx-auto max-w-2xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Solicita tu Cotización</h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              Completa el formulario y te contactaremos en menos de 24 horas con una propuesta personalizada.
            </p>
          </div>
          <Card className="border-2 border-gray-200 shadow-xl">
            <CardHeader>
              <CardTitle className="text-2xl font-bold text-gray-900">Formulario de Cotización</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleQuoteSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700">Nombre Completo *</label>
                    <Input 
                      required
                      placeholder="Tu nombre"
                      value={quoteForm.name}
                      onChange={(e) => setQuoteForm({...quoteForm, name: e.target.value})}
                      className="border-2 border-gray-200 focus:border-orange-500"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700">Correo Electrónico *</label>
                    <Input 
                      required
                      type="email"
                      placeholder="tu@email.com"
                      value={quoteForm.email}
                      onChange={(e) => setQuoteForm({...quoteForm, email: e.target.value})}
                      className="border-2 border-gray-200 focus:border-orange-500"
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700">Teléfono *</label>
                    <Input 
                      required
                      type="tel"
                      placeholder="300 123 4567"
                      value={quoteForm.phone}
                      onChange={(e) => setQuoteForm({...quoteForm, phone: e.target.value})}
                      className="border-2 border-gray-200 focus:border-orange-500"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700">Tipo de Contenedor *</label>
                    <Select value={quoteForm.containerType} onValueChange={(value) => setQuoteForm({...quoteForm, containerType: value})}>
                      <SelectTrigger className="border-2 border-gray-200 focus:border-orange-500">
                        <SelectValue placeholder="Selecciona un tipo" />
                      </SelectTrigger>
                      <SelectContent>
                        {containerTypes.map((container) => (
                          <SelectItem key={container.id} value={container.name}>
                            {container.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-gray-700">Mensaje / Detalles del Proyecto</label>
                  <Textarea 
                    placeholder="Cuéntanos sobre tu proyecto, necesidades específicas, ubicación, etc."
                    rows={5}
                    value={quoteForm.message}
                    onChange={(e) => setQuoteForm({...quoteForm, message: e.target.value})}
                    className="border-2 border-gray-200 focus:border-orange-500 resize-none"
                  />
                </div>
                <Button type="submit" className="w-full bg-orange-600 hover:bg-orange-700 text-white font-semibold py-6 text-lg">
                  Enviar Solicitud de Cotización
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contacto" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="container mx-auto">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Contáctanos</h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                Estamos aquí para responder todas tus preguntas y ayudarte a encontrar la solución perfecta.
              </p>
            </div>
            
            <div className="grid lg:grid-cols-2 gap-12">
              {/* Contact Info */}
              <div className="space-y-8">
                <Card className="border-2 border-gray-100 hover:shadow-lg transition-all duration-300 group">
                  <CardHeader>
                    <div className="flex items-start gap-4">
                      <div className="w-14 h-14 bg-orange-100 rounded-xl flex items-center justify-center group-hover:bg-orange-600 transition-colors duration-300">
                        <Phone className="w-7 h-7 text-orange-600 group-hover:text-white transition-colors duration-300" />
                      </div>
                      <div>
                        <CardTitle className="text-xl font-bold text-gray-900 mb-2">Teléfono</CardTitle>
                        <CardDescription className="text-base">
                          <a href="tel:+573001234567" className="text-orange-600 hover:text-orange-700 font-semibold">
                            +57 300 123 4567
                          </a>
                          <br />
                          <a href="tel:+5744441234" className="text-orange-600 hover:text-orange-700 font-semibold">
                            +57 (4) 444 1234
                          </a>
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                </Card>

                <Card className="border-2 border-gray-100 hover:shadow-lg transition-all duration-300 group">
                  <CardHeader>
                    <div className="flex items-start gap-4">
                      <div className="w-14 h-14 bg-orange-100 rounded-xl flex items-center justify-center group-hover:bg-orange-600 transition-colors duration-300">
                        <Mail className="w-7 h-7 text-orange-600 group-hover:text-white transition-colors duration-300" />
                      </div>
                      <div>
                        <CardTitle className="text-xl font-bold text-gray-900 mb-2">Email</CardTitle>
                        <CardDescription className="text-base">
                          <a href="mailto:info@trioncontainers.com" className="text-orange-600 hover:text-orange-700 font-semibold">
                            info@trioncontainers.com
                          </a>
                          <br />
                          <a href="mailto:ventas@trioncontainers.com" className="text-orange-600 hover:text-orange-700 font-semibold">
                            ventas@trioncontainers.com
                          </a>
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                </Card>

                <Card className="border-2 border-gray-100 hover:shadow-lg transition-all duration-300 group">
                  <CardHeader>
                    <div className="flex items-start gap-4">
                      <div className="w-14 h-14 bg-orange-100 rounded-xl flex items-center justify-center group-hover:bg-orange-600 transition-colors duration-300">
                        <MapPinIcon className="w-7 h-7 text-orange-600 group-hover:text-white transition-colors duration-300" />
                      </div>
                      <div>
                        <CardTitle className="text-xl font-bold text-gray-900 mb-2">Ubicación</CardTitle>
                        <CardDescription className="text-base text-gray-600 leading-relaxed">
                          Carrera 43A #1-50, Patio Bonito<br />
                          Medellín, Antioquia<br />
                          Colombia
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                </Card>

                <Card className="border-2 border-orange-600 bg-gradient-to-br from-orange-50 to-white hover:shadow-lg transition-all duration-300">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-xl font-bold text-gray-900 mb-2">Horario de Atención</CardTitle>
                        <CardDescription className="text-base text-gray-700">
                          Lunes - Viernes: 7:00 AM - 6:00 PM<br />
                          Sábados: 8:00 AM - 2:00 PM<br />
                          Domingos y Festivos: Cerrado
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                </Card>
              </div>

              {/* Contact Form */}
              <Card className="border-2 border-gray-200 shadow-xl">
                <CardHeader>
                  <CardTitle className="text-2xl font-bold text-gray-900">Envíanos un Mensaje</CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleContactSubmit} className="space-y-6">
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-gray-700">Nombre Completo *</label>
                      <Input 
                        required
                        placeholder="Tu nombre"
                        value={contactForm.name}
                        onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                        className="border-2 border-gray-200 focus:border-orange-500"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-gray-700">Correo Electrónico *</label>
                      <Input 
                        required
                        type="email"
                        placeholder="tu@email.com"
                        value={contactForm.email}
                        onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                        className="border-2 border-gray-200 focus:border-orange-500"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-gray-700">Teléfono *</label>
                      <Input 
                        required
                        type="tel"
                        placeholder="300 123 4567"
                        value={contactForm.phone}
                        onChange={(e) => setContactForm({...contactForm, phone: e.target.value})}
                        className="border-2 border-gray-200 focus:border-orange-500"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-gray-700">Mensaje *</label>
                      <Textarea 
                        required
                        placeholder="¿En qué podemos ayudarte?"
                        rows={5}
                        value={contactForm.message}
                        onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                        className="border-2 border-gray-200 focus:border-orange-500 resize-none"
                      />
                    </div>
                    <Button type="submit" className="w-full bg-orange-600 hover:bg-orange-700 text-white font-semibold py-6 text-lg">
                      Enviar Mensaje
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="container mx-auto">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Encuéntranos</h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                Visítanos en nuestra sede principal en Medellín o contáctanos para coordinar una visita.
              </p>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-2xl border-4 border-white">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3966.1234567890!2d-75.5812!3d6.2442!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNsKwMTQnMzkuMSJOIDc1wrAzNCw1Mi4zIlc!5e0!3m2!1sen!2sco!4v1234567890"
                width="100%"
                height="450"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Ubicación TRION CONTAINERS en Medellín"
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
            <div>
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center">
                  <Package className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold">TRION</h3>
                  <p className="text-xs text-orange-400 font-medium">CONTAINERS</p>
                </div>
              </div>
              <p className="text-gray-400 leading-relaxed mb-4">
                Espacios Modulares. Soluciones que Construyen.
              </p>
              <p className="text-gray-500 text-sm">
                Líderes en contenedores marítimos en Colombia desde 2008.
              </p>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-4 text-white">Enlaces Rápidos</h4>
              <ul className="space-y-3">
                <li><a href="#inicio" className="text-gray-400 hover:text-orange-400 transition-colors">Inicio</a></li>
                <li><a href="#nosotros" className="text-gray-400 hover:text-orange-400 transition-colors">Sobre Nosotros</a></li>
                <li><a href="#servicios" className="text-gray-400 hover:text-orange-400 transition-colors">Servicios</a></li>
                <li><a href="#catalogo" className="text-gray-400 hover:text-orange-400 transition-colors">Catálogo</a></li>
                <li><a href="#proyectos" className="text-gray-400 hover:text-orange-400 transition-colors">Proyectos</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-4 text-white">Servicios</h4>
              <ul className="space-y-3">
                <li className="text-gray-400">Alquiler de Contenedores</li>
                <li className="text-gray-400">Venta de Contenedores</li>
                <li className="text-gray-400">Proyectos Personalizados</li>
                <li className="text-gray-400">Transporte y Logística</li>
                <li className="text-gray-400">Mantenimiento</li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-4 text-white">Contacto</h4>
              <ul className="space-y-3">
                <li className="flex items-start gap-2">
                  <Phone className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <a href="tel:+573001234567" className="text-gray-400 hover:text-orange-400 transition-colors block">+57 300 123 4567</a>
                    <a href="tel:+5744441234" className="text-gray-400 hover:text-orange-400 transition-colors block">+57 (4) 444 1234</a>
                  </div>
                </li>
                <li className="flex items-start gap-2">
                  <Mail className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                  <a href="mailto:info@trioncontainers.com" className="text-gray-400 hover:text-orange-400 transition-colors">
                    info@trioncontainers.com
                  </a>
                </li>
                <li className="flex items-start gap-2">
                  <MapPinIcon className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-400">
                    Medellín, Antioquia<br />Colombia
                  </span>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-gray-500 text-sm">
                © 2025 TRION CONTAINERS. Todos los derechos reservados.
              </p>
              <div className="flex gap-4">
                <a href="#" className="text-gray-500 hover:text-orange-400 transition-colors text-sm">Política de Privacidad</a>
                <span className="text-gray-700">|</span>
                <a href="#" className="text-gray-500 hover:text-orange-400 transition-colors text-sm">Términos y Condiciones</a>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* WhatsApp Floating Button */}
      <button
        onClick={openWhatsApp}
        className="fixed bottom-8 right-8 w-16 h-16 bg-green-500 hover:bg-green-600 rounded-full shadow-2xl flex items-center justify-center z-50 transition-all duration-300 hover:scale-110 group"
        aria-label="Contactar por WhatsApp"
      >
        <MessageCircle className="w-8 h-8 text-white group-hover:scale-110 transition-transform" />
        <span className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white text-xs font-bold animate-pulse">
          1
        </span>
      </button>
    </div>
  );
};

export default Home;